// src/components/family/profile/UpcomingAppointmentsCard.tsx
// Upcoming appointments card for child profile overview

import React from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  ChevronRight,
  Clock,
  MapPin,
  Video,
  Phone,
  Building2,
  Plus,
  Bell,
  CheckCircle,
} from 'lucide-react';
import { format, isToday, isTomorrow, differenceInDays, parseISO } from 'date-fns';
import type { Appointment } from '@/types';

interface UpcomingAppointmentsCardProps {
  appointments: Appointment[];
  childId: string;
  isLoading?: boolean;
  className?: string;
}

// Location type icons
const locationIcons = {
  in_person: Building2,
  telehealth: Video,
  phone: Phone,
} as const;

// Format appointment date with smart relative display
const formatAppointmentDate = (date: Date): { day: string; month: string; relative?: string } => {
  if (isToday(date)) {
    return {
      day: format(date, 'd'),
      month: format(date, 'MMM'),
      relative: 'Today',
    };
  }
  
  if (isTomorrow(date)) {
    return {
      day: format(date, 'd'),
      month: format(date, 'MMM'),
      relative: 'Tomorrow',
    };
  }
  
  const daysUntil = differenceInDays(date, new Date());
  
  return {
    day: format(date, 'd'),
    month: format(date, 'MMM'),
    relative: daysUntil <= 7 ? format(date, 'EEEE') : undefined,
  };
};

const AppointmentItem: React.FC<{ appointment: Appointment }> = ({
  appointment,
}) => {
  const startDate = parseISO(appointment.startsAt);
  const dateInfo = formatAppointmentDate(startDate);
  const LocationIcon = locationIcons[appointment.locationType as keyof typeof locationIcons] || Building2;

  // Determine urgency styling
  const isUrgent = isToday(startDate) || isTomorrow(startDate);

  return (
    <Link
      to={`/family/appointments/${appointment.id}`}
      className={`
        flex gap-4 p-3 rounded-xl transition-all duration-200 group
        ${isUrgent 
          ? 'bg-terracotta/5 hover:bg-terracotta/10 border border-terracotta/20' 
          : 'hover:bg-cream-dark'
        }
      `}
    >
      {/* Date block */}
      <div
        className={`
          w-14 h-14 rounded-xl flex flex-col items-center justify-center flex-shrink-0
          ${isUrgent 
            ? 'bg-terracotta text-white' 
            : 'bg-sage/10 text-sage-dark'
          }
        `}
      >
        <span className="text-lg font-display font-bold leading-none">
          {dateInfo.day}
        </span>
        <span className="text-xs font-medium uppercase">
          {dateInfo.month}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-medium text-navy truncate group-hover:text-sage-dark transition-colors">
            {appointment.title}
          </h4>
          {dateInfo.relative && (
            <span
              className={`
                px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide flex-shrink-0
                ${isUrgent 
                  ? 'bg-terracotta/20 text-terracotta' 
                  : 'bg-sage/10 text-sage-dark'
                }
              `}
            >
              {dateInfo.relative}
            </span>
          )}
        </div>

        <p className="text-sm text-warm-gray truncate mt-0.5">
          {appointment.providerName || 'Provider TBD'}
        </p>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-warm-gray">
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {format(startDate, 'h:mm a')}
          </span>
          <span className="flex items-center gap-1">
            <LocationIcon className="w-3.5 h-3.5" />
            {appointment.locationType === 'telehealth' 
              ? 'Video Visit' 
              : appointment.locationName || 'Location TBD'
            }
          </span>
        </div>
      </div>

      {/* Arrow */}
      <ChevronRight className="w-5 h-5 text-warm-gray/50 opacity-0 group-hover:opacity-100 flex-shrink-0 self-center transition-opacity" />
    </Link>
  );
};

export const UpcomingAppointmentsCard: React.FC<UpcomingAppointmentsCardProps> = ({
  appointments,
  childId,
  isLoading = false,
  className = '',
}) => {
  if (isLoading) {
    return <UpcomingAppointmentsCardSkeleton />;
  }

  // Sort by date and take upcoming only
  const upcomingAppointments = appointments
    .filter((apt) => new Date(apt.startsAt) >= new Date())
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime())
    .slice(0, 4);

  return (
    <div className={`bg-white rounded-2xl border border-cream-dark ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-cream-dark">
        <h3 className="text-lg font-display font-semibold text-navy flex items-center gap-2">
          <Calendar className="w-5 h-5 text-terracotta" />
          Upcoming
        </h3>
        <Link
          to={`/family/children/${childId}/appointments`}
          className="text-sm text-sage font-medium hover:text-sage-dark flex items-center gap-1 transition-colors"
        >
          Calendar
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Appointments list */}
      <div className="p-3 space-y-2">
        {upcomingAppointments.length > 0 ? (
          upcomingAppointments.map((appointment) => (
            <AppointmentItem key={appointment.id} appointment={appointment} />
          ))
        ) : (
          <EmptyAppointmentsState childId={childId} />
        )}
      </div>

      {/* Footer with quick add */}
      <div className="px-4 py-3 border-t border-cream-dark bg-cream/30">
        <Link
          to={`/family/children/${childId}/appointments/new`}
          className="flex items-center justify-center gap-2 w-full py-2 border-2 border-dashed border-cream-dark rounded-xl text-sm font-medium text-warm-gray hover:border-sage/40 hover:text-sage-dark transition-colors"
        >
          <Plus className="w-4 h-4" />
          Schedule Appointment
        </Link>
      </div>
    </div>
  );
};

// Empty state
const EmptyAppointmentsState: React.FC<{ childId: string }> = ({ childId }) => (
  <div className="py-6 text-center">
    <div className="w-12 h-12 rounded-full bg-cream-dark flex items-center justify-center mx-auto mb-3">
      <Calendar className="w-6 h-6 text-warm-gray" />
    </div>
    <p className="text-sm text-warm-gray mb-3">No upcoming appointments</p>
    <Link
      to={`/family/children/${childId}/appointments/new`}
      className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg font-medium text-sm hover:bg-sage-dark transition-colors"
    >
      <Plus className="w-4 h-4" />
      Add Appointment
    </Link>
  </div>
);

// Loading skeleton
export const UpcomingAppointmentsCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-2xl border border-cream-dark animate-pulse">
    <div className="flex items-center justify-between px-4 py-3 border-b border-cream-dark">
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 bg-cream-dark rounded" />
        <div className="h-6 bg-cream-dark rounded w-24" />
      </div>
      <div className="h-4 bg-cream-dark rounded w-16" />
    </div>

    <div className="p-3 space-y-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex gap-4 p-3">
          <div className="w-14 h-14 rounded-xl bg-cream-dark" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-cream-dark rounded w-3/4" />
            <div className="h-3 bg-cream-dark rounded w-1/2" />
            <div className="flex gap-4">
              <div className="h-3 bg-cream-dark rounded w-16" />
              <div className="h-3 bg-cream-dark rounded w-24" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default UpcomingAppointmentsCard;
